import { defineComponent, h, mergeProps } from 'vue'
import type { IconAliases, IconSet } from 'vuetify'

type SvgChild = { tag: string; attrs: Record<string, string | number> }

// Define your custom SVG icons here
// The key will be the icon name used in <v-icon>nc:iconName</v-icon>
// An icon may be a single `path` (filled with currentColor) or a list of
// explicit `children` (rect/path/etc.) when it needs strokes + fills together.
const svgIcons: Record<string, { viewBox: string; path?: string; children?: SvgChild[] }> = {
  'layers-single': {
    path: 'M12,19L3,12L12,5L21,12L12,19M12,7.53L6.26,12L12,16.47L17.74,12L12,7.53Z',
    viewBox: '0 0 24 24'
  },
  'home-layout': {
    path: 'M6,2H18A2,2 0 0,1 20,4V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2M13,4V20H18V4H13Z',
    viewBox: '0 0 24 24'
  },
  // Table with the top (header) row filled solid; body outlined.
  'table-header-row': {
    viewBox: '0 0 24 24',
    children: [
      {
        tag: 'rect',
        attrs: {
          x: 4,
          y: 4,
          width: 16,
          height: 16,
          rx: 2.5,
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 1.6
        }
      },
      {
        tag: 'path',
        attrs: {
          d: 'M6.5,4 H17.5 A2.5,2.5 0 0 1 20,6.5 V9.5 H4 V6.5 A2.5,2.5 0 0 1 6.5,4 Z',
          fill: 'currentColor'
        }
      }
    ]
  },
  // Table with the left (header) column filled solid; body outlined.
  'table-header-column': {
    viewBox: '0 0 24 24',
    children: [
      {
        tag: 'rect',
        attrs: {
          x: 4,
          y: 4,
          width: 16,
          height: 16,
          rx: 2.5,
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 1.6
        }
      },
      {
        tag: 'path',
        attrs: {
          d: 'M6.5,4 H9.5 V20 H6.5 A2.5,2.5 0 0 1 4,17.5 V6.5 A2.5,2.5 0 0 1 6.5,4 Z',
          fill: 'currentColor'
        }
      }
    ]
  }
  // Add more custom icons here if needed
  // 'another-icon': { path: '...', viewBox: '0 0 24 24' },
}

// Mirror Vuetify's own VSvgIcon component exactly (vuetify/lib/composables/
// icons.js): render the <svg class="v-icon__svg"> INSIDE props.tag (the <i> that
// receives the `.v-icon` + size classes via attrs), rather than returning a bare
// <svg>. That's what makes `.v-icon__svg { width/height: 100% }` resolve against
// the 1em icon box instead of the button — so these custom icons size, align, and
// color identically to native Vuetify SVG icons (and the material-symbols set).
const NcSvgIcon = defineComponent({
  name: 'NcSvgIcon',
  inheritAttrs: false,
  props: {
    tag: { type: String, default: 'i' },
    icon: { type: [String, Array], default: undefined }
  },
  setup(props, { attrs }) {
    return () => {
      const iconName = props.icon as string
      const iconData = svgIcons[iconName]

      if (!iconData) {
        console.warn(`Custom icon "${iconName}" not found in "nc" set.`)
        // Render nothing rather than fall back to an MDI icon.
        return null
      }

      const children = iconData.children
        ? iconData.children.map((child) => h(child.tag, child.attrs))
        : [h('path', { d: iconData.path })]

      return h(props.tag, mergeProps(attrs, { style: null }), {
        default: () => [
          h(
            'svg',
            {
              class: 'v-icon__svg nc-custom-svg-icon',
              xmlns: 'http://www.w3.org/2000/svg',
              viewBox: iconData.viewBox,
              role: 'img',
              'aria-hidden': 'true'
            },
            children
          )
        ]
      })
    }
  }
})

const ncIconSet: IconSet = {
  // Cast: our props (tag/icon) are a structural subset of Vuetify's IconProps,
  // so the runtime contract holds; the mismatch is purely TS's stricter shape.
  component: NcSvgIcon as unknown as IconSet['component']
}

// Vuetify's IconAliases type expects certain keys. Set to empty strings
// to satisfy the type and avoid MDI references.
const aliases: IconAliases = {
  collapse: '',
  complete: '',
  cancel: '',
  close: '',
  delete: '',
  clear: '',
  success: '',
  info: '',
  warning: '',
  error: '',
  prev: '',
  next: '',
  checkboxOn: '',
  checkboxOff: '',
  checkboxIndeterminate: '',
  delimiter: '',
  sortAsc: '',
  sortDesc: '',
  sort: '',
  expand: '',
  menu: '',
  subgroup: '',
  dropdown: '',
  radioOn: '',
  radioOff: '',
  edit: '',
  ratingEmpty: '',
  ratingFull: '',
  ratingHalf: '',
  loading: '',
  first: '',
  last: '',
  unfold: '',
  file: '',
  plus: '',
  minus: '',
  calendar: '',
  treeviewCollapse: '',
  treeviewExpand: '',
  eyeDropper: '',
  upload: '',
  color: '',
  command: '',
  ctrl: '',
  space: '',
  shift: '',
  alt: '',
  enter: '',
  arrowup: '',
  arrowdown: '',
  arrowleft: '',
  arrowright: '',
  backspace: ''
}

export { aliases, ncIconSet }
